import React from 'react';
import type { Payment, PaymentRefund } from '../types/payment.types';
import { formatPaymentDate } from '../utils/paymentUtils';
import {
  REFUND_OPERATION_LABEL,
  REFUND_ROW_STATUS_LABEL,
  formatMoney,
  isVoidedPayment,
  refundReasonLabel,
  refundSummary,
  toCents,
} from '../utils/refunds';

/** SAM-466: refunded / remaining totals and the refund ledger rows of one payment. */

const STATUS_TONE: Record<PaymentRefund['status'], string> = {
  succeeded: 'bg-ok-bg text-ok-fg',
  pending: 'bg-warn-bg text-warn-fg',
  unknown: 'bg-warn-bg text-warn-fg',
  failed: 'bg-bad-bg text-bad-fg',
  rejected: 'bg-surface-3 text-ink-700',
};

export const PaymentRefundTotals: React.FC<{ payment: Payment }> = ({ payment }) => {
  const s = refundSummary(payment);
  // A void is not a refund: no «Refunded / Remaining» here. The Status chip and the history already say Voided.
  if (isVoidedPayment(payment)) return null;
  return (
    <div className="grid grid-cols-2 gap-3 text-sm" data-testid="payment-refund-totals">
      <div className="rounded-lg border border-line bg-surface p-3">
        <div className="text-xs text-ink-500">Refunded</div>
        <div className="font-semibold text-ink-900">{formatMoney(s.refundedCents, s.currency)}</div>
      </div>
      <div className="rounded-lg border border-line bg-surface p-3">
        <div className="text-xs text-ink-500">Remaining</div>
        <div className="font-semibold text-ink-900">{formatMoney(s.remainingCents, s.currency)}</div>
      </div>
    </div>
  );
};

export const PaymentRefundHistory: React.FC<{ payment: Payment }> = ({ payment }) => {
  const rows = payment.refunds || [];
  const currency = refundSummary(payment).currency;
  const voided = isVoidedPayment(payment);
  return (
    <div className="flex flex-col gap-2" data-testid="payment-refund-history">
      <div className="text-[13px] font-semibold text-ink-700">{voided ? 'History' : 'Refund history'}</div>
      {rows.length === 0 ? (
        <div className="text-[13px] text-ink-500">{voided ? 'No movements recorded.' : 'No refunds on this payment.'}</div>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {[...rows].reverse().map((r) => (
            <li key={r.id} className="rounded-lg border border-line p-3 text-[13px]">
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-ink-900">
                  {voided && r.operation === 'void' ? 'Voided' : REFUND_OPERATION_LABEL[r.operation] || r.operation} ·{' '}
                  {formatMoney(toCents(r.amount), r.currency || currency)}
                </span>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_TONE[r.status] || ''}`}>
                  {REFUND_ROW_STATUS_LABEL[r.status] || r.status}
                </span>
              </div>
              {r.reason && <div className="mt-1 whitespace-pre-wrap break-words text-ink-700">{refundReasonLabel(r.reason)}</div>}
              <div className="mt-1 text-xs text-ink-500">
                {formatPaymentDate(r.created_at || null)}
                {r.gateway_reference ? <span className="font-mono"> · {r.gateway_reference}</span> : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
